import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  StyleSheet,
  ScrollView,
  Platform,
  ActivityIndicator,
  Modal,
  Pressable,
  Alert,
  useWindowDimensions,
  Image as RNImage,
} from 'react-native';
import { StatusBar } from 'expo-status-bar';
import { Image } from 'expo-image';
import * as ImagePicker from 'expo-image-picker';
import { useTheme } from '../context/ThemeContext';
import HamburgerMenu from '../components/HamburgerMenu';
import ProfileIcon from '../components/ProfileIcon';
import Stopwatch from '../components/Stopwatch';
import CourseMediaCarousel, { createCourseMediaCarouselStyles } from '../components/CourseMediaCarousel';
import ModalVideoEmbed from '../components/ModalVideoEmbed';
import { ExternalBreaksCarousel } from './MyCourseDetailPage';
import { getApiUrl } from '../utils/apiConfig';
import {
  formatYoutubeUploadError,
  isYoutubeReconnectError,
  uploadYoutubeBinaryFromBase64,
} from '../utils/youtubeBinaryUpload';
import {
  assertVideoUploadable,
  getYoutubeVideoPickerOptions,
} from '../utils/videoUploadLimits';
import YoutubeOAuthBar from '../components/YoutubeOAuthBar';
import { dedupeCoursesForDropdown } from '../utils/coursesCatalog';
import {
  BASE_COURSE_TYPES,
  buildCourseTypeOptions,
  canonicalCourseType,
  collapseCourseTypeSpaces,
} from '../utils/courseTypes';
import {
  getSupabaseClient,
  getCourseImageBucketConfigMeta,
  getSupabaseConfigHint,
  getSupabaseProjectUrl,
} from '../utils/supabase';
import { removeSupabaseObjectByPublicUrl } from '../utils/supabasePublicStorage';
import { resolveCourseGalleryEntryToUrl, courseNameToFilePrefix, buildSupabasePublicObjectUrl } from '../utils/courseMediaUrls';

/** Web: scope global.css overrides for pending file rows (dark mode). */
const WEB_DS_PENDING_FILE =
  Platform.OS === 'web' ? { dataSet: { courseEditorPendingFile: 'true' } } : {};
const WEB_DS_PENDING_REMOVE =
  Platform.OS === 'web' ? { dataSet: { courseEditorPendingRemove: 'true' } } : {};

const CAROUSEL_ASPECT = 16 / 9;
const CAROUSEL_WIDTH_RATIO = 0.6;
const BREAK_UPLOAD_TABS = [
  { id: 'youtube', label: 'YouTube URL' },
  { id: 'facebook', label: 'Facebook URL' },
  { id: 'file', label: 'File Upload' },
];

const TOAST_MSG_VIDEO_TITLE = 'Name the Video title for this course.';
const TOAST_MSG_SAVE_OK = 'Video saved to course breaks.';

const EXT_THUMB_TILE_WIDTH = 92;
const EXT_THUMB_TILE_GAP = 10;

function normalizeMediaList(raw) {
  if (raw == null) return [];
  let data = raw;
  if (typeof raw === 'string') {
    try {
      data = JSON.parse(raw);
    } catch {
      const t = raw.trim();
      return t ? [t] : [];
    }
  }
  if (!Array.isArray(data)) return [];
  return data.map((u) => String(u ?? '').trim()).filter(Boolean);
}

/**
 * Persist gallery entries as absolute https URLs when they are Supabase Storage objects.
 * Leaves `/…` API paths unchanged. Coerces bare keys to full public object URLs.
 */
function persistCourseMediaDisplayList(entries, bucketId) {
  const projectUrl = getSupabaseProjectUrl();
  const out = [];
  for (const e of entries) {
    const s = String(e ?? '').trim();
    if (!s) continue;
    if (/^https:\/\//i.test(s)) {
      out.push(s);
      continue;
    }
    if (/^http:\/\//i.test(s)) {
      try {
        const u = new URL(s);
        if (u.hostname.endsWith('supabase.co')) {
          u.protocol = 'https:';
          out.push(u.toString());
          continue;
        }
      } catch {
        /* keep below */
      }
      out.push(s);
      continue;
    }
    if (s.startsWith('/')) {
      out.push(s);
      continue;
    }
    if (projectUrl && bucketId) {
      const full = buildSupabasePublicObjectUrl(projectUrl, bucketId, s);
      if (full) out.push(full);
    }
  }
  return out;
}

function normalizeExternalBreakLinks(raw) {
  if (raw == null) return [];
  let data = raw;
  if (typeof raw === 'string') {
    try {
      data = JSON.parse(raw);
    } catch {
      return [];
    }
  }
  if (!Array.isArray(data)) {
    if (typeof data === 'object' && data !== null) {
      data = Object.entries(data).map(([k, v]) => ({
        break_name: k,
        url: String(v ?? ''),
      }));
    } else {
      return [];
    }
  }
  const out = [];
  for (const item of data) {
    if (!item || typeof item !== 'object') continue;
    const name = String(item.break_name ?? item.key ?? item.name ?? '').trim();
    const url = String(item.url ?? item.value ?? '').trim();
    if (!url) continue;
    out.push({ break_name: name || 'Break', url });
  }
  return out;
}

/** @returns {string | null} */
function parseYouTubeVideoId(url) {
  if (!url || typeof url !== 'string') return null;
  const s = url.trim();
  const m =
    s.match(
      /(?:https?:\/\/)?(?:(?:www|m)\.)?youtube\.com\/(?:watch\?v=|embed\/|shorts\/)([^&?#/]+)/i
    ) || s.match(/(?:https?:\/\/)?youtu\.be\/([^&?#/]+)/i);
  return m ? m[1] : null;
}

function youtubeEmbedUrl(videoId) {
  if (!videoId) return '';
  const q = 'autoplay=1&playsinline=1&rel=0';
  return `https://www.youtube.com/embed/${encodeURIComponent(videoId)}?${q}`;
}

function resolvePlaybackUri(url) {
  if (!url || typeof url !== 'string') return '';
  const id = parseYouTubeVideoId(url);
  if (id) return youtubeEmbedUrl(id);
  return url.trim();
}

function resolveCourseMediaUri(url) {
  if (!url || typeof url !== 'string') return url;
  const s = url.trim();
  if (/^https?:\/\//i.test(s)) return s;
  if (s.startsWith('/')) return `${getApiUrl()}${s}`;
  return s;
}

function resolveExternalPlaybackUri(url) {
  if (!url || typeof url !== 'string') return '';
  if (parseYouTubeVideoId(url)) return resolvePlaybackUri(url);
  return resolveCourseMediaUri(url);
}

function isLikelyImageUrl(url) {
  if (!url || typeof url !== 'string') return false;
  if (parseYouTubeVideoId(url)) return false;
  const noQuery = url.split('?')[0].toLowerCase();
  if (/\.(mp4|webm|mov|m4v|mpeg|mpg)$/i.test(noQuery)) return false;
  if (
    url.includes('/storage/v1/object/public/') &&
    /\.(mp4|webm|mov|m4v|mpeg|mpg)(\?|$)/i.test(noQuery)
  ) {
    return false;
  }
  const path = url.split('?')[0].toLowerCase();
  if (path.includes('/assets/')) return true;
  return /\.(jpe?g|png|gif|webp)$/i.test(path.trim());
}

function isFacebookUrl(url) {
  if (!url || typeof url !== 'string') return false;
  return /facebook\.com|fb\.com/i.test(url);
}

function sanitizeUploadFilename(name) {
  const raw = String(name || '').replace(/^.*[/\\]/, '');
  const cleaned = raw.replace(/[^a-zA-Z0-9._-]+/g, '_').replace(/^_+|_+$/g, '');
  return (cleaned || 'file').slice(0, 180);
}

function guessMimeFromName(name) {
  const lower = String(name).toLowerCase();
  if (lower.endsWith('.png')) return 'image/png';
  if (lower.endsWith('.webp')) return 'image/webp';
  if (lower.endsWith('.gif')) return 'image/gif';
  if (lower.endsWith('.heic')) return 'image/heic';
  if (lower.endsWith('.mp4')) return 'video/mp4';
  if (lower.endsWith('.mov')) return 'video/quicktime';
  if (lower.endsWith('.webm')) return 'video/webm';
  if (lower.endsWith('.mpeg') || lower.endsWith('.mpg')) return 'video/mpeg';
  if (lower.endsWith('.jpg') || lower.endsWith('.jpeg')) return 'image/jpeg';
  return 'application/octet-stream';
}

/** Image picker often omits extension / MIME; Storage may reject octet-stream. */
function normalizeCourseImageForUpload(fileName, mimeType) {
  let safe = sanitizeUploadFilename(fileName);
  const hasImgExt = /\.(jpe?g|png|gif|webp|heic)$/i.test(safe);
  let ct = (mimeType && String(mimeType).trim()) || '';
  if (!ct.startsWith('image/')) {
    ct = guessMimeFromName(safe);
  }
  if (!ct.startsWith('image/') || ct === 'application/octet-stream') {
    ct = 'image/jpeg';
  }
  if (!hasImgExt) {
    const ext =
      ct === 'image/png'
        ? 'png'
        : ct === 'image/webp'
          ? 'webp'
          : ct === 'image/gif'
            ? 'gif'
            : ct === 'image/heic'
              ? 'heic'
              : 'jpg';
    const base = safe.replace(/\.[^.]+$/, '').replace(/^_+|_+$/g, '');
    safe = `${base || 'image'}.${ext}`;
  }
  return { safe, contentType: ct };
}

/**
 * Supabase object key: `{course_slug}_{filename}` (filename = sanitized original).
 * Duplicates in the same attach batch become `{slug}_{base}_2.{ext}`, etc.
 */
function buildCourseImageStorageKey(courseNamePrefix, safeFilename, usedKeys) {
  const prefix = courseNamePrefix;
  let key = `${prefix}_${safeFilename}`;
  if (!usedKeys.has(key)) {
    usedKeys.add(key);
    return key;
  }
  const dot = safeFilename.lastIndexOf('.');
  const base = dot > 0 ? safeFilename.slice(0, dot) : safeFilename;
  const ext = dot > 0 ? safeFilename.slice(dot) : '';
  let n = 2;
  while (usedKeys.has(`${prefix}_${base}_${n}${ext}`)) n += 1;
  key = `${prefix}_${base}_${n}${ext}`;
  usedKeys.add(key);
  return key;
}

function formatStorageUploadError(error) {
  if (!error) return 'Storage upload failed';
  const msg = error.message || 'Storage upload failed';
  const status = typeof error.status === 'number' ? error.status : null;
  const sc =
    error.statusCode != null && String(error.statusCode).trim() !== ''
      ? String(error.statusCode)
      : '';
  const parts = [msg];
  if (status != null) parts.push(`HTTP ${status}`);
  if (sc && sc !== String(status)) parts.push(sc);
  return parts.join(' — ');
}

async function uriToArrayBuffer(uri) {
  const res = await fetch(uri);
  if (!res.ok) throw new Error(`Could not read file (${res.status})`);
  return res.arrayBuffer();
}

async function putCourse(courseId, patch) {
  const res = await fetch(
    `${getApiUrl()}/api/courses/${encodeURIComponent(String(courseId))}`,
    {
      method: 'PUT',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(patch),
    }
  );
  const raw = await res.text();
  let body = {};
  try {
    body = raw ? JSON.parse(raw) : {};
  } catch {
    /* ignore */
  }
  if (!res.ok) throw new Error(body.error || `HTTP ${res.status}`);
  return body;
}

function CourseEditorPickerModal({
  visible,
  title,
  onClose,
  theme,
  styles,
  children,
  footer,
}) {
  return (
    <Modal visible={visible} animationType="fade" transparent onRequestClose={onClose}>
      <View
        style={styles.uploadModalRoot}
        {...(Platform.OS === 'web' ? { dataSet: { courseEditorModalRoot: 'true' } } : {})}
      >
        <Pressable
          style={styles.uploadModalBackdrop}
          onPress={onClose}
          {...(Platform.OS === 'web' ? { dataSet: { modalBackdrop: 'true' } } : {})}
        />
        <View
          style={[
            styles.uploadModalSheet,
            styles.coursePickerSheet,
            {
              backgroundColor: theme.isDarkMode ? theme.colors.surface : '#fff',
              borderColor: theme.isDarkMode ? theme.colors.border : '#ddd',
            },
          ]}
        >
          <Text
            style={[styles.uploadModalTitle, { color: theme.colors.heading }]}
            {...(Platform.OS === 'web' ? { dataSet: { courseEditorModalTitle: 'true' } } : {})}
          >
            {title}
          </Text>
          <ScrollView
            style={styles.coursePickerScroll}
            nestedScrollEnabled
            keyboardShouldPersistTaps="handled"
            {...(Platform.OS === 'web' ? { dataSet: { courseEditorPickerScroll: 'true' } } : {})}
          >
            {children}
          </ScrollView>
          {footer ? <View style={styles.pickerModalFooter}>{footer}</View> : null}
        </View>
      </View>
    </Modal>
  );
}

export default function CourseEditorPage({ navigation, route }) {
  const theme = useTheme();
  const stopwatchRef = useRef(null);
  const user = route?.params?.user ?? null;
  const { width: windowWidth } = useWindowDimensions();
  const slideWidth = Math.max(1, Math.round(windowWidth * CAROUSEL_WIDTH_RATIO));
  const slideHeight = Math.round(slideWidth / CAROUSEL_ASPECT);

  const [currentUser, setCurrentUser] = useState(user);
  const [coursesList, setCoursesList] = useState([]);
  const [selectedCourseId, setSelectedCourseId] = useState(null);
  const [loadingCourse, setLoadingCourse] = useState(false);
  const [courseRow, setCourseRow] = useState(null);

  const [mediaUrls, setMediaUrls] = useState([]);
  const [summaryDraft, setSummaryDraft] = useState('');
  const [externalBreakLinks, setExternalBreakLinks] = useState([]);

  const [pendingCourseImages, setPendingCourseImages] = useState([]);
  const [courseImagesUploading, setCourseImagesUploading] = useState(false);

  const [summarySaving, setSummarySaving] = useState(false);

  const [videoModal, setVideoModal] = useState(null);
  const [uploadVideoModalVisible, setUploadVideoModalVisible] = useState(false);
  const [uploadModalTab, setUploadModalTab] = useState('youtube');
  const [breakUploadToast, setBreakUploadToast] = useState(null);

  const [ytUrlInput, setYtUrlInput] = useState('');
  const [ytInspect, setYtInspect] = useState(null);
  const [ytVideoTitle, setYtVideoTitle] = useState('');

  const [fbUrlInput, setFbUrlInput] = useState('');
  const [fbVideoTitle, setFbVideoTitle] = useState('');

  const [filePickMeta, setFilePickMeta] = useState(null);
  const [fileBase64, setFileBase64] = useState(null);
  const [fileVideoTitle, setFileVideoTitle] = useState('');

  const webBreakVideoInputRef = useRef(null);
  const [ytCheckLoading, setYtCheckLoading] = useState(false);

  const youtubeOAuthRef = useRef(null);

  const [breaksSaving, setBreaksSaving] = useState(false);
  const [coursePickerVisible, setCoursePickerVisible] = useState(false);
  const [courseTypeDraft, setCourseTypeDraft] = useState(null);
  const [courseTypePickerVisible, setCourseTypePickerVisible] = useState(false);
  const [customCourseTypes, setCustomCourseTypes] = useState([]);
  const [newCourseTypeInput, setNewCourseTypeInput] = useState('');

  const styles = useMemo(() => getStyles(theme, slideWidth), [theme, slideWidth]);

  const refreshCoursesList = useCallback(async () => {
    try {
      const r = await fetch(`${getApiUrl()}/api/courses`);
      if (!r.ok) return;
      const data = await r.json();
      setCoursesList(dedupeCoursesForDropdown(Array.isArray(data) ? data : []));
    } catch {
      /* ignore */
    }
  }, []);

  const courseTypeOptions = useMemo(
    () => buildCourseTypeOptions(coursesList, customCourseTypes),
    [coursesList, customCourseTypes]
  );

  useEffect(() => {
    const uid = route?.params?.userId || user?.id || 1;
    fetch(`${getApiUrl()}/api/users/${encodeURIComponent(String(uid))}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data) setCurrentUser(data);
      })
      .catch(() => {});
  }, [route?.params?.userId, user?.id]);

  useEffect(() => {
    fetch(`${getApiUrl()}/api/courses`)
      .then((r) => (r.ok ? r.json() : []))
      .then((data) => setCoursesList(dedupeCoursesForDropdown(Array.isArray(data) ? data : [])))
      .catch(() => setCoursesList([]));
  }, []);

  useEffect(() => {
    if (!courseRow) {
      setCourseTypeDraft(null);
      return;
    }
    setCourseTypeDraft(canonicalCourseType(courseRow.course_type));
  }, [courseRow?.id, courseRow?.course_type]);

  useEffect(() => {
    const cid = route?.params?.courseId;
    if (cid != null) {
      setSelectedCourseId(Number(cid));
    }
  }, [route?.params?.courseId]);

  useEffect(() => {
    let cancelled = false;
    async function loadCourse() {
      if (selectedCourseId == null) {
        setCourseRow(null);
        setMediaUrls([]);
        setSummaryDraft('');
        setExternalBreakLinks([]);
        setPendingCourseImages([]);
        return;
      }
      setLoadingCourse(true);
      try {
        const r = await fetch(
          `${getApiUrl()}/api/courses/${encodeURIComponent(String(selectedCourseId))}`
        );
        if (!r.ok) throw new Error(String(r.status));
        const row = await r.json();
        if (cancelled) return;
        setCourseRow(row);
        setMediaUrls(normalizeMediaList(row?.course_media_display));
        const rawSum = row?.summary;
        setSummaryDraft(rawSum == null ? '' : String(rawSum));
        setExternalBreakLinks(normalizeExternalBreakLinks(row?.external_break_links));
      } catch {
        if (!cancelled) {
          setCourseRow(null);
          Alert.alert('Error', 'Could not load course.');
        }
      } finally {
        if (!cancelled) setLoadingCourse(false);
      }
    }
    void loadCourse();
    return () => {
      cancelled = true;
    };
  }, [selectedCourseId]);

  const goBuilders = () => {
    navigation.navigate('BuilderMain', {
      userId: currentUser?.id ?? user?.id ?? 1,
      user: currentUser ?? user,
    });
  };

  const openVideoModal = useCallback((uri, title = 'Video', options = {}) => {
    if (!uri) return;
    setVideoModal({
      uri,
      title: title || 'Video',
      isImage: !!options.isImage,
    });
  }, []);

  const closeVideoModal = useCallback(() => setVideoModal(null), []);

  const handlePickCourseImages = async () => {
    try {
      const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (!perm.granted) {
        Alert.alert('Permission needed', 'Allow photo library access to attach images.');
        return;
      }
      const result = await ImagePicker.launchImageLibraryAsync({
        mediaTypes: ImagePicker.MediaTypeOptions.Images,
        allowsMultipleSelection: true,
        quality: 0.85,
      });
      if (result.canceled) return;
      const assets = result.assets || [];
      const batch = assets.map((a, i) => ({
        id: `${Date.now()}-${i}-${Math.random().toString(36).slice(2, 9)}`,
        uri: a.uri,
        fileName: a.fileName || a.uri.split(/[/\\]/).pop() || `image_${i}.jpg`,
        mimeType: a.mimeType ?? null,
      }));
      setPendingCourseImages((prev) => [...prev, ...batch]);
    } catch (e) {
      Alert.alert('Images', e.message || 'Could not open photo library.');
    }
  };

  const removePendingCourseImage = (id) => {
    setPendingCourseImages((prev) => prev.filter((p) => p.id !== id));
  };

  const handleAttachCourseImages = async () => {
    if (!courseRow?.id || pendingCourseImages.length === 0) return;
    const supabase = getSupabaseClient();
    if (!supabase) {
      Alert.alert('Storage', getSupabaseConfigHint().join('\n') || 'Supabase is not configured.');
      return;
    }
    setCourseImagesUploading(true);
    try {
      const bucketMeta = getCourseImageBucketConfigMeta();
      let supabaseHost = '';
      try {
        const u = getSupabaseProjectUrl();
        if (u) supabaseHost = new URL(u).host;
      } catch {
        supabaseHost = '(invalid EXPO_PUBLIC_SUPABASE_URL)';
      }
      console.info('[CourseEditor] Course images → Supabase Storage', {
        bucketId: bucketMeta.bucketId,
        configSource: bucketMeta.source,
        supabaseHost,
        note: 'Uses anon key only; Storage RLS must allow INSERT for anon.',
      });

      const bucket = bucketMeta.bucketId;
      const newUrls = [];
      const usedStorageKeys = new Set();
      for (let i = 0; i < pendingCourseImages.length; i += 1) {
        const img = pendingCourseImages[i];
        const { safe, contentType } = normalizeCourseImageForUpload(img.fileName, img.mimeType);
        const prefix = courseNameToFilePrefix(courseRow.course_name);
        const path = buildCourseImageStorageKey(prefix, safe, usedStorageKeys);
        const body = await uriToArrayBuffer(img.uri);
        if (!body || body.byteLength === 0) {
          throw new Error('Could not read image data (empty file).');
        }
        const { error } = await supabase.storage.from(bucket).upload(path, body, {
          contentType,
          upsert: false,
        });
        if (error) {
          console.warn('[CourseEditor] course image upload failed', {
            bucket,
            bucketMeta,
            path,
            contentType,
            message: error.message,
            status: error.status,
            statusCode: error.statusCode,
          });
          throw new Error(formatStorageUploadError(error));
        }
        const { data: pub } = supabase.storage.from(bucket).getPublicUrl(path);
        const url = pub?.publicUrl ? String(pub.publicUrl).trim() : '';
        if (url) newUrls.push(url.replace(/^http:\/\//i, 'https://'));
      }
      const merged = persistCourseMediaDisplayList([...mediaUrls, ...newUrls], bucket);
      const updated = await putCourse(courseRow.id, { course_media_display: merged });
      const saved = normalizeMediaList(updated?.course_media_display);
      if (merged.length > 0 && saved.length === 0) {
        throw new Error(
          'The server did not save course images (empty course_media_display). Ensure the API is updated (jsonb fix) and try again.'
        );
      }
      setMediaUrls(saved);
      setPendingCourseImages([]);
      Alert.alert('Done', 'Course images attached.');
    } catch (e) {
      const msg = e.message || 'Could not upload images.';
      const meta = getCourseImageBucketConfigMeta();
      const hint = [
        `Target bucket id: "${meta.bucketId}" (config: ${meta.source}).`,
        'Supabase Dashboard → Storage: bucket id must match exactly. For public gallery URLs, bucket should be public; storage.objects policies must allow INSERT (and usually SELECT) for anon — this app does not use supabase.auth.',
        'Not the same as a raw AWS S3 bucket unless you only use Supabase’s Storage API to that project.',
      ].join('\n');
      Alert.alert('Upload failed', `${msg}\n\n${hint}`);
    } finally {
      setCourseImagesUploading(false);
    }
  };

  const handleRemoveCourseImage = useCallback(
    (url, index) => {
      if (!courseRow?.id) return;
      Alert.alert('Remove image', 'Remove this image from the course and storage (when applicable)?', [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Remove',
          style: 'destructive',
          onPress: async () => {
            try {
              const supabase = getSupabaseClient();
              if (supabase) {
                await removeSupabaseObjectByPublicUrl(supabase, url);
              }
              const bucket = getCourseImageBucketConfigMeta().bucketId;
              const next = persistCourseMediaDisplayList(
                mediaUrls.filter((_, i) => i !== index),
                bucket
              );
              await putCourse(courseRow.id, { course_media_display: next });
              setMediaUrls(next);
            } catch (e) {
              Alert.alert('Error', e.message || 'Could not remove image.');
            }
          },
        },
      ]);
    },
    [courseRow?.id, mediaUrls]
  );

  const handleSaveSummary = async () => {
    if (!courseRow?.id) return;
    setSummarySaving(true);
    try {
      const trimmed = summaryDraft.trim();
      const typeValue = courseTypeDraft ? collapseCourseTypeSpaces(courseTypeDraft) : null;
      const updated = await putCourse(courseRow.id, {
        summary: trimmed === '' ? null : trimmed,
        course_type: typeValue,
      });
      setCourseRow((prev) =>
        prev
          ? {
              ...prev,
              summary: trimmed === '' ? null : trimmed,
              course_type: typeValue,
              ...(updated?.course_type !== undefined ? { course_type: updated.course_type } : {}),
            }
          : prev
      );
      if (typeValue) {
        setCustomCourseTypes((prev) => {
          const k = typeValue.toLowerCase();
          if (prev.some((t) => String(t).toLowerCase() === k)) return prev;
          if (BASE_COURSE_TYPES.some((t) => t.toLowerCase() === k)) return prev;
          return [...prev, typeValue];
        });
      }
      await refreshCoursesList();
      Alert.alert('Saved', 'Course summary and type updated.');
    } catch (e) {
      Alert.alert('Error', e.message || 'Could not save course.');
    } finally {
      setSummarySaving(false);
    }
  };

  const handleAddCourseType = () => {
    const trimmed = collapseCourseTypeSpaces(newCourseTypeInput);
    if (!trimmed) {
      Alert.alert('Course type', 'Enter a name for the new course type.');
      return;
    }
    const canonical = canonicalCourseType(trimmed) || trimmed;
    setCourseTypeDraft(canonical);
    setCustomCourseTypes((prev) => {
      const k = canonical.toLowerCase();
      if (prev.some((t) => String(t).toLowerCase() === k)) return prev;
      if (BASE_COURSE_TYPES.some((t) => t.toLowerCase() === k)) return prev;
      return [...prev, canonical];
    });
    setNewCourseTypeInput('');
    setCourseTypePickerVisible(false);
  };

  const handleRemoveBreak = useCallback(
    (row, index) => {
      if (!courseRow?.id) return;
      Alert.alert('Remove media', 'Remove this item from course breaks and storage (when applicable)?', [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Remove',
          style: 'destructive',
          onPress: async () => {
            try {
              const url = row.url;
              if (!parseYouTubeVideoId(url)) {
                const supabase = getSupabaseClient();
                if (supabase) {
                  await removeSupabaseObjectByPublicUrl(supabase, url);
                }
              }
              const next = externalBreakLinks.filter((_, i) => i !== index);
              await putCourse(courseRow.id, { external_break_links: next });
              setExternalBreakLinks(next);
            } catch (e) {
              Alert.alert('Error', e.message || 'Could not remove media.');
            }
          },
        },
      ]);
    },
    [courseRow?.id, externalBreakLinks]
  );

  const resetBreakUploadModal = useCallback(() => {
    setUploadModalTab('youtube');
    setBreakUploadToast(null);
    setYtUrlInput('');
    setYtInspect(null);
    setYtVideoTitle('');
    setFbUrlInput('');
    setFbVideoTitle('');
    setFilePickMeta(null);
    setFileBase64(null);
    setFileVideoTitle('');
    setYtCheckLoading(false);
  }, []);

  const markYoutubeNeedsReconnect = useCallback((message) => {
    youtubeOAuthRef.current?.markNeedsReconnect?.(message);
    setBreakUploadToast({
      type: 'error',
      message:
        message ||
        'YouTube upload access expired. Tap Reconnect YouTube above, then try again.',
    });
  }, []);

  const appendExternalBreakLink = async (row) => {
    if (!courseRow?.id) return;
    const merged = [...externalBreakLinks, row];
    await putCourse(courseRow.id, { external_break_links: merged });
    setExternalBreakLinks(merged);
  };

  const inspectYoutubeOrSync = async (urlTrimmed) => {
    const res = await fetch(`${getApiUrl()}/api/youtube/video-info`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ url: urlTrimmed, account_key: 'default' }),
    });
    const data = await res.json().catch(() => ({}));
    if (!res.ok) throw new Error(data.error || data.message || 'Could not check video');
    return { ...data, checkedUrl: urlTrimmed };
  };

  const runYoutubeCheck = async () => {
    const url = ytUrlInput.trim();
    if (!parseYouTubeVideoId(url)) {
      Alert.alert('YouTube', 'Enter a valid YouTube URL.');
      return;
    }
    setYtCheckLoading(true);
    setBreakUploadToast(null);
    try {
      const data = await inspectYoutubeOrSync(url);
      setYtInspect(data);
      if (data.onMainChannel) {
        setYtVideoTitle(data.title ? String(data.title) : '');
      } else {
        setYtVideoTitle('');
      }
    } catch (e) {
      Alert.alert('YouTube', e.message || 'Check failed.');
      setYtInspect(null);
    } finally {
      setYtCheckLoading(false);
    }
  };

  const saveYoutubeTab = async () => {
    if (!courseRow?.id) return;
    const url = ytUrlInput.trim();
    if (!parseYouTubeVideoId(url)) {
      Alert.alert('YouTube', 'Enter a valid YouTube URL.');
      return;
    }
    setBreaksSaving(true);
    setBreakUploadToast(null);
    try {
      let inspect = ytInspect;
      if (!inspect || inspect.checkedUrl !== url) {
        inspect = await inspectYoutubeOrSync(url);
        setYtInspect(inspect);
      }
      if (inspect.onMainChannel) {
        const name = (inspect.title && String(inspect.title).trim()) || 'Video';
        const linkUrl = inspect.canonicalUrl || url;
        await appendExternalBreakLink({ break_name: name, url: linkUrl });
      } else {
        const title = ytVideoTitle.trim();
        if (!title) {
          setBreakUploadToast({ type: 'error', message: TOAST_MSG_VIDEO_TITLE });
          return;
        }
        const uploadResponse = await fetch(`${getApiUrl()}/api/youtube/upload`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            sourceUrl: url,
            title,
            description: `Course break: ${courseRow.course_name || ''}`,
            privacyStatus: 'unlisted',
            isShort: true,
            account_key: 'default',
          }),
        });
        const uploadData = await uploadResponse.json().catch(() => ({}));
        if (!uploadResponse.ok) {
          const detail = formatYoutubeUploadError(uploadData, 'Upload failed');
          if (isYoutubeReconnectError(detail) || uploadData.code === 'youtube_invalid_grant') {
            markYoutubeNeedsReconnect(detail);
          }
          const setupHint = /yt-dlp/i.test(String(detail))
            ? '\n\nInstall yt-dlp on the server and restart the API.'
            : '';
          throw new Error(`${detail}${setupHint}`);
        }
        const y = uploadData.youtubeUrl;
        if (!y) throw new Error('No YouTube URL returned');
        await appendExternalBreakLink({ break_name: title, url: y });
      }
      setYtUrlInput('');
      setYtInspect(null);
      setYtVideoTitle('');
      setBreakUploadToast({ type: 'success', message: TOAST_MSG_SAVE_OK });
    } catch (e) {
      Alert.alert('Save failed', e.message || 'Could not save video.');
    } finally {
      setBreaksSaving(false);
    }
  };

  const saveFacebookTab = async () => {
    if (!courseRow?.id) return;
    const url = fbUrlInput.trim();
    if (!isFacebookUrl(url)) {
      Alert.alert('Facebook', 'Enter a Facebook video URL.');
      return;
    }
    const title = fbVideoTitle.trim();
    if (!title) {
      setBreakUploadToast({ type: 'error', message: TOAST_MSG_VIDEO_TITLE });
      return;
    }
    setBreaksSaving(true);
    setBreakUploadToast(null);
    try {
      const uploadResponse = await fetch(`${getApiUrl()}/api/youtube/upload`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sourceUrl: url,
          title,
          description: `Course break: ${courseRow.course_name || ''}`,
          privacyStatus: 'unlisted',
          isShort: true,
          account_key: 'default',
        }),
      });
      const uploadData = await uploadResponse.json().catch(() => ({}));
      if (!uploadResponse.ok) {
        const detail = formatYoutubeUploadError(uploadData, 'Upload failed');
        if (isYoutubeReconnectError(detail) || uploadData.code === 'youtube_invalid_grant') {
          markYoutubeNeedsReconnect(detail);
        }
        const setupHint = /yt-dlp/i.test(String(detail))
          ? '\n\nInstall yt-dlp on the server and restart the API.'
          : '';
        throw new Error(`${detail}${setupHint}`);
      }
      const y = uploadData.youtubeUrl;
      if (!y) throw new Error('No YouTube URL returned');
      await appendExternalBreakLink({ break_name: title, url: y });
      setFbUrlInput('');
      setFbVideoTitle('');
      setBreakUploadToast({ type: 'success', message: TOAST_MSG_SAVE_OK });
    } catch (e) {
      Alert.alert('Save failed', e.message || 'Could not save video.');
    } finally {
      setBreaksSaving(false);
    }
  };

  const saveFileTab = async () => {
    if (!courseRow?.id) return;
    if (!fileBase64 || !filePickMeta) {
      Alert.alert('File', 'Choose a video file first.');
      return;
    }
    const title = fileVideoTitle.trim();
    if (!title) {
      setBreakUploadToast({ type: 'error', message: TOAST_MSG_VIDEO_TITLE });
      return;
    }
    setBreaksSaving(true);
    setBreakUploadToast(null);
    try {
      const y = await uploadYoutubeBinaryFromBase64({
        fileBase64,
        title,
        description: `Course break: ${courseRow.course_name || ''}`,
        privacyStatus: 'unlisted',
        isShort: true,
        account_key: 'default',
      });
      await appendExternalBreakLink({ break_name: title, url: y.youtubeUrl });
      setFilePickMeta(null);
      setFileBase64(null);
      setFileVideoTitle('');
      setBreakUploadToast({ type: 'success', message: TOAST_MSG_SAVE_OK });
    } catch (e) {
      const msg = e.message || 'Could not upload video.';
      if (isYoutubeReconnectError(msg)) {
        markYoutubeNeedsReconnect(msg);
      }
      Alert.alert('Save failed', msg);
    } finally {
      setBreaksSaving(false);
    }
  };

  const afterBreakVideoFileChosen = useCallback(async () => {
    await youtubeOAuthRef.current?.refresh?.();
  }, []);

  const pickBreakVideoFile = async () => {
    try {
      const perm = await ImagePicker.requestMediaLibraryPermissionsAsync();
      if (!perm.granted) {
        Alert.alert('Permission needed', 'Allow library access to attach a video.');
        return;
      }
      const result = await ImagePicker.launchImageLibraryAsync(getYoutubeVideoPickerOptions());
      if (result.canceled || !result.assets?.[0]) return;
      const a = result.assets[0];
      const fileName = a.fileName || a.uri.split(/[/\\]/).pop() || 'video.mp4';
      setFilePickMeta({ fileName });
      setFileVideoTitle('');
      if (a.base64) {
        const sizeMsg = assertVideoUploadable({ fileSize: a.fileSize, base64: a.base64 });
        if (sizeMsg) {
          Alert.alert('Video too large', sizeMsg);
          return;
        }
        setFileBase64(a.base64);
        void afterBreakVideoFileChosen();
        return;
      }
      const response = await fetch(a.uri);
      const blob = await response.blob();
      const b64 = await new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const s = reader.result;
          if (typeof s === 'string' && s.includes(',')) resolve(s.split(',')[1]);
          else reject(new Error('Could not read file'));
        };
        reader.onerror = reject;
        reader.readAsDataURL(blob);
      });
      const sizeMsg = assertVideoUploadable({ fileSize: a.fileSize || blob.size, base64: b64 });
      if (sizeMsg) {
        Alert.alert('Video too large', sizeMsg);
        return;
      }
      setFileBase64(b64);
      void afterBreakVideoFileChosen();
    } catch (e) {
      Alert.alert('Video', e.message || 'Could not pick video.');
    }
  };

  const onWebBreakVideoFileChange = (e) => {
    const file = e.target?.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => {
      const s = reader.result;
      if (typeof s === 'string' && s.includes(',')) {
        setFileBase64(s.split(',')[1]);
        setFilePickMeta({ fileName: file.name || 'video' });
        setFileVideoTitle('');
        void afterBreakVideoFileChosen();
      }
    };
    reader.readAsDataURL(file);
    e.target.value = '';
  };

  useEffect(() => {
    if (!uploadVideoModalVisible) return;
    void youtubeOAuthRef.current?.refresh?.();
  }, [uploadVideoModalVisible]);

  const breakEditable = !!courseRow;

  const resolvedCourseGalleryUrls = useMemo(
    () => mediaUrls.map((e) => resolveCourseGalleryEntryToUrl(e)).filter(Boolean),
    [mediaUrls]
  );

  return (
    <View
      style={styles.container}
      data-theme={theme.isDarkMode ? 'dark' : 'light'}
      {...(Platform.OS === 'web' ? { dataSet: { screen: 'course-editor' } } : {})}
    >
      <StatusBar style={theme.isDarkMode ? 'light' : 'dark'} />
      <View style={styles.header} data-header="true">
        <View style={styles.hamburgerContainer} data-header-element="hamburger">
          <HamburgerMenu navigation={navigation} stopwatchRef={stopwatchRef} />
        </View>
        <TouchableOpacity
          style={styles.breadcrumbContainer}
          onPress={goBuilders}
          activeOpacity={0.7}
          accessibilityRole="button"
          accessibilityLabel="Back to Builders"
        >
          <Text style={styles.breadcrumbBack} numberOfLines={1}>
            ‹ Builders
          </Text>
        </TouchableOpacity>
        <View style={styles.profileIconContainer} data-header-element="profile">
          <ProfileIcon navigation={navigation} user={currentUser ?? user} />
        </View>
      </View>

      <ScrollView
        style={styles.content}
        contentContainerStyle={[styles.contentContainer, Platform.OS === 'web' && styles.contentContainerWeb]}
        showsVerticalScrollIndicator={false}
        nestedScrollEnabled
      >
        <Text style={[styles.pageTitle, { color: theme.colors.heading }]}>Course Editor</Text>

        <View style={styles.fieldContainer}>
          <Text style={styles.label}>Select Course</Text>
          <View nativeID="course-editor-course-dropdown-trigger" style={styles.selectCourseTrigger}>
            <TouchableOpacity
              style={styles.selectCourseTriggerInner}
              onPress={() => setCoursePickerVisible(true)}
              activeOpacity={0.85}
              accessibilityRole="button"
              accessibilityLabel="Select course"
              {...(Platform.OS === 'web' ? { dataSet: { dropdownTrigger: 'true' } } : {})}
            >
              <Text
                style={[styles.selectCourseTriggerText, selectedCourseId == null && styles.selectCoursePlaceholder]}
                numberOfLines={2}
              >
                {selectedCourseId == null
                  ? '— Choose a course —'
                  : coursesList.find((c) => c.id === selectedCourseId)?.course_name ?? 'Course'}
              </Text>
              <Text style={styles.selectCourseTriggerChevron}>▼</Text>
            </TouchableOpacity>
          </View>
        </View>

        {selectedCourseId != null && !loadingCourse && courseRow ? (
          <View style={styles.fieldContainer}>
            <Text style={styles.label}>Course Type</Text>
            <View nativeID="course-editor-type-dropdown-trigger" style={styles.selectCourseTrigger}>
              <TouchableOpacity
                style={styles.selectCourseTriggerInner}
                onPress={() => setCourseTypePickerVisible(true)}
                activeOpacity={0.85}
                accessibilityRole="button"
                accessibilityLabel="Select course type"
                {...(Platform.OS === 'web' ? { dataSet: { dropdownTrigger: 'true' } } : {})}
              >
                <Text
                  style={[
                    styles.selectCourseTriggerText,
                    !courseTypeDraft && styles.selectCoursePlaceholder,
                  ]}
                  numberOfLines={2}
                >
                  {courseTypeDraft || '— None —'}
                </Text>
                <Text style={styles.selectCourseTriggerChevron}>▼</Text>
              </TouchableOpacity>
            </View>
          </View>
        ) : null}

        {selectedCourseId != null && loadingCourse ? (
          <View style={styles.centerBlock}>
            <ActivityIndicator size="large" color={theme.colors.heading} />
          </View>
        ) : null}

        {selectedCourseId != null && !loadingCourse && courseRow ? (
          <>
            <View style={styles.titleRow}>
              <Text style={[styles.courseTitle, { color: theme.colors.heading }]} numberOfLines={3}>
                {courseRow.course_name}
              </Text>
            </View>

            {mediaUrls.length > 0 ? (
              <CourseMediaCarousel
                mediaUrls={resolvedCourseGalleryUrls}
                slideWidth={slideWidth}
                slideHeight={slideHeight}
                theme={theme}
                styles={styles}
                onOpenVideo={openVideoModal}
                pauseAutoRotate={!!videoModal}
                showRemoveButton
                onRemoveSlide={handleRemoveCourseImage}
              />
            ) : (
              <Text style={[styles.helperMuted, { color: theme.colors.text }]}>
                No course images yet. Use the button below to add some.
              </Text>
            )}

            <View style={styles.fieldContainer}>
              <TouchableOpacity
                style={styles.uploadButton}
                onPress={handlePickCourseImages}
                activeOpacity={0.85}
                disabled={courseImagesUploading}
              >
                <Text style={styles.uploadButtonText}>Upload new course images</Text>
              </TouchableOpacity>
              {pendingCourseImages.map((img) => (
                <View key={img.id} style={styles.pendingRow} {...WEB_DS_PENDING_FILE}>
                  <Text style={styles.pendingName} numberOfLines={1}>
                    {img.fileName}
                  </Text>
                  <TouchableOpacity
                    onPress={() => removePendingCourseImage(img.id)}
                    hitSlop={12}
                    disabled={courseImagesUploading}
                    accessibilityRole="button"
                    style={styles.pendingRemoveHit}
                    {...WEB_DS_PENDING_REMOVE}
                  >
                    <Text style={styles.pendingRemove}>✕</Text>
                  </TouchableOpacity>
                </View>
              ))}
              {pendingCourseImages.length > 0 ? (
                <TouchableOpacity
                  style={[styles.button, styles.saveButton, { marginTop: 14 }]}
                  onPress={() => void handleAttachCourseImages()}
                  disabled={courseImagesUploading}
                  activeOpacity={0.85}
                >
                  {courseImagesUploading ? (
                    <ActivityIndicator color={theme.isDarkMode ? '#000' : '#fff'} />
                  ) : (
                    <Text style={styles.saveButtonText}>Attach images to course</Text>
                  )}
                </TouchableOpacity>
              ) : null}
            </View>

            <View style={styles.summaryBlock}>
              <Text style={[styles.summaryHeading, { color: theme.colors.heading }]}>
                Course Summary
              </Text>
              <TextInput
                style={[styles.summaryTextArea, { color: theme.colors.text, borderColor: theme.colors.border }]}
                value={summaryDraft}
                onChangeText={setSummaryDraft}
                placeholder="Course summary"
                placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
                multiline
                textAlignVertical="top"
              />
              <TouchableOpacity
                style={[styles.button, styles.saveButton, { marginTop: 12, alignSelf: 'stretch' }]}
                onPress={() => void handleSaveSummary()}
                disabled={summarySaving}
                activeOpacity={0.85}
              >
                {summarySaving ? (
                  <ActivityIndicator color={theme.isDarkMode ? '#000' : '#fff'} />
                ) : (
                  <Text style={styles.saveButtonText}>Save</Text>
                )}
              </TouchableOpacity>
            </View>

            <View style={styles.extSection}>
              <Text style={[styles.extHeading, { color: theme.colors.heading }]}>Course breaks</Text>
              {externalBreakLinks.length > 0 ? (
                <ExternalBreaksCarousel
                  links={externalBreakLinks}
                  theme={theme}
                  styles={styles}
                  slideWidth={slideWidth}
                  slideHeight={slideHeight}
                  onOpenVideo={openVideoModal}
                  editable={breakEditable}
                  onRemoveBreak={breakEditable ? handleRemoveBreak : undefined}
                  hideSectionHeading
                  compactSection
                />
              ) : (
                <Text style={[styles.helperMuted, { color: theme.colors.text, marginBottom: 12 }]}>
                  No course breaks media yet.
                </Text>
              )}

              {breakEditable ? (
                <>
                  <TouchableOpacity
                    style={[styles.uploadButton, { marginBottom: 12 }]}
                    onPress={() => {
                      resetBreakUploadModal();
                      setUploadVideoModalVisible(true);
                    }}
                    activeOpacity={0.85}
                    disabled={breaksSaving}
                  >
                    <Text style={styles.uploadButtonText}>Upload video</Text>
                  </TouchableOpacity>
                </>
              ) : null}
            </View>
          </>
        ) : null}
      </ScrollView>

      <CourseEditorPickerModal
        visible={coursePickerVisible}
        title="Select Course"
        onClose={() => setCoursePickerVisible(false)}
        theme={theme}
        styles={styles}
      >
        <TouchableOpacity
          style={[styles.dropdownOption, selectedCourseId === null && styles.dropdownOptionSelected]}
          onPress={() => {
            setSelectedCourseId(null);
            setCoursePickerVisible(false);
          }}
          activeOpacity={0.85}
          {...(Platform.OS === 'web'
            ? {
                dataSet: {
                  dropdownRow: 'true',
                  ...(selectedCourseId === null ? { pickerRowSelected: 'true' } : {}),
                },
              }
            : {})}
        >
          <Text
            style={[
              styles.dropdownOptionText,
              selectedCourseId === null && styles.dropdownOptionTextSelected,
            ]}
          >
            — Choose a course —
          </Text>
        </TouchableOpacity>
        {coursesList.map((c) => (
          <TouchableOpacity
            key={String(c.id)}
            style={[styles.dropdownOption, selectedCourseId === c.id && styles.dropdownOptionSelected]}
            onPress={() => {
              setSelectedCourseId(c.id);
              setCoursePickerVisible(false);
            }}
            activeOpacity={0.85}
            {...(Platform.OS === 'web'
              ? {
                  dataSet: {
                    dropdownRow: 'true',
                    ...(selectedCourseId === c.id ? { pickerRowSelected: 'true' } : {}),
                  },
                }
              : {})}
          >
            <Text
              style={[
                styles.dropdownOptionText,
                selectedCourseId === c.id && styles.dropdownOptionTextSelected,
              ]}
              numberOfLines={3}
            >
              {c.course_name}
            </Text>
          </TouchableOpacity>
        ))}
      </CourseEditorPickerModal>

      <CourseEditorPickerModal
        visible={courseTypePickerVisible}
        title="Course Type"
        onClose={() => {
          setCourseTypePickerVisible(false);
          setNewCourseTypeInput('');
        }}
        theme={theme}
        styles={styles}
        footer={
          <View style={styles.addCourseTypeRow}>
            <TextInput
              style={[
                styles.addCourseTypeInput,
                { color: theme.colors.text, borderColor: theme.colors.border },
              ]}
              value={newCourseTypeInput}
              onChangeText={setNewCourseTypeInput}
              placeholder="New course type"
              placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
              onSubmitEditing={handleAddCourseType}
              returnKeyType="done"
              {...(Platform.OS === 'web' ? { dataSet: { courseEditorNewTypeInput: 'true' } } : {})}
            />
            <TouchableOpacity
              style={styles.addCourseTypeBtn}
              onPress={handleAddCourseType}
              activeOpacity={0.85}
              {...(Platform.OS === 'web' ? { dataSet: { courseEditorNewTypeAdd: 'true' } } : {})}
            >
              <Text style={styles.addCourseTypeBtnText}>Add</Text>
            </TouchableOpacity>
          </View>
        }
      >
        <TouchableOpacity
          style={[styles.dropdownOption, courseTypeDraft == null && styles.dropdownOptionSelected]}
          onPress={() => {
            setCourseTypeDraft(null);
            setCourseTypePickerVisible(false);
          }}
          activeOpacity={0.85}
          {...(Platform.OS === 'web'
            ? {
                dataSet: {
                  dropdownRow: 'true',
                  ...(courseTypeDraft == null ? { pickerRowSelected: 'true' } : {}),
                },
              }
            : {})}
        >
          <Text
            style={[
              styles.dropdownOptionText,
              courseTypeDraft == null && styles.dropdownOptionTextSelected,
            ]}
          >
            — None —
          </Text>
        </TouchableOpacity>
        {courseTypeOptions.map((typeName) => (
          <TouchableOpacity
            key={typeName}
            style={[
              styles.dropdownOption,
              courseTypeDraft != null &&
                courseTypeDraft.toLowerCase() === typeName.toLowerCase() &&
                styles.dropdownOptionSelected,
            ]}
            onPress={() => {
              setCourseTypeDraft(typeName);
              setCourseTypePickerVisible(false);
            }}
            activeOpacity={0.85}
            {...(Platform.OS === 'web'
              ? {
                  dataSet: {
                    dropdownRow: 'true',
                    ...(courseTypeDraft != null &&
                    courseTypeDraft.toLowerCase() === typeName.toLowerCase()
                      ? { pickerRowSelected: 'true' }
                      : {}),
                  },
                }
              : {})}
          >
            <Text
              style={[
                styles.dropdownOptionText,
                courseTypeDraft != null &&
                  courseTypeDraft.toLowerCase() === typeName.toLowerCase() &&
                  styles.dropdownOptionTextSelected,
              ]}
            >
              {typeName}
            </Text>
          </TouchableOpacity>
        ))}
      </CourseEditorPickerModal>

      <Modal
        visible={uploadVideoModalVisible}
        animationType="fade"
        transparent
        onRequestClose={() => {
          resetBreakUploadModal();
          setUploadVideoModalVisible(false);
        }}
      >
        <View
          style={styles.uploadModalRoot}
          {...(Platform.OS === 'web' ? { dataSet: { courseEditorModalRoot: 'true' } } : {})}
        >
          <Pressable
            style={styles.uploadModalBackdrop}
            onPress={() => {
              resetBreakUploadModal();
              setUploadVideoModalVisible(false);
            }}
            {...(Platform.OS === 'web' ? { dataSet: { modalBackdrop: 'true' } } : {})}
          />
          <View
            style={[
              styles.uploadModalSheet,
              {
                backgroundColor: theme.isDarkMode ? theme.colors.surface : '#fff',
                borderColor: theme.isDarkMode ? theme.colors.border : '#ddd',
              },
            ]}
          >
            <Text
              style={[styles.uploadModalTitle, { color: theme.colors.heading }]}
              {...(Platform.OS === 'web' ? { dataSet: { courseEditorModalTitle: 'true' } } : {})}
            >
              Upload video
            </Text>

            {breakUploadToast ? (
              <View
                style={[
                  styles.breakUploadToast,
                  breakUploadToast.type === 'error'
                    ? styles.breakUploadToastError
                    : styles.breakUploadToastSuccess,
                ]}
                {...(Platform.OS === 'web'
                  ? {
                      dataSet: {
                        courseEditorBreakUploadToast: 'true',
                        breakToastType: breakUploadToast.type,
                      },
                    }
                  : {})}
              >
                <Text style={styles.breakUploadToastMessage}>{breakUploadToast.message}</Text>
                <TouchableOpacity
                  onPress={() => setBreakUploadToast(null)}
                  style={styles.breakUploadToastClose}
                  hitSlop={{ top: 8, bottom: 8, left: 8, right: 8 }}
                  accessibilityRole="button"
                  accessibilityLabel="Dismiss"
                >
                  <Text style={styles.breakUploadToastCloseText}>✕</Text>
                </TouchableOpacity>
              </View>
            ) : null}

            <YoutubeOAuthBar ref={youtubeOAuthRef} />

            <View style={styles.breakUploadTabs}>
              {BREAK_UPLOAD_TABS.map((t) => {
                const active = uploadModalTab === t.id;
                return (
                  <TouchableOpacity
                    key={t.id}
                    style={[styles.breakUploadTab, active && styles.breakUploadTabActive]}
                    onPress={() => {
                      setUploadModalTab(t.id);
                      setBreakUploadToast(null);
                    }}
                    activeOpacity={0.85}
                    accessibilityRole="tab"
                    accessibilityState={{ selected: active }}
                  >
                    <Text
                      style={[
                        styles.breakUploadTabLabel,
                        { color: active ? theme.colors.heading : theme.colors.text },
                        active && styles.breakUploadTabLabelActive,
                      ]}
                      numberOfLines={2}
                    >
                      {t.label}
                    </Text>
                  </TouchableOpacity>
                );
              })}
            </View>

            <ScrollView
              style={styles.breakUploadScroll}
              keyboardShouldPersistTaps="handled"
              showsVerticalScrollIndicator={false}
            >
              {uploadModalTab === 'youtube' ? (
                <>
                  <Text
                    style={styles.label}
                    {...(Platform.OS === 'web' ? { dataSet: { courseEditorUploadFieldLabel: 'true' } } : {})}
                  >
                    YouTube URL
                  </Text>
                  <TextInput
                    style={[styles.input, ytInspect?.onMainChannel ? styles.inputLockedGrey : null]}
                    value={ytUrlInput}
                    onChangeText={(s) => {
                      setYtUrlInput(s);
                      setYtInspect(null);
                    }}
                    placeholder="https://www.youtube.com/watch?v=…"
                    placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
                    autoCapitalize="none"
                    autoCorrect={false}
                    editable={!ytInspect?.onMainChannel}
                  />
                  {ytInspect?.onMainChannel ? (
                    <View style={styles.breakUploadHintRow}>
                      <Text style={[styles.breakUploadChannelHint, { color: theme.colors.text }]}>
                        This video is already on your main YouTube channel. The link will be saved as-is.
                      </Text>
                      <TouchableOpacity
                        onPress={() => setYtInspect(null)}
                        hitSlop={12}
                        accessibilityRole="button"
                        accessibilityLabel="Change URL"
                      >
                        <Text style={{ color: theme.colors.heading, fontWeight: '700', fontSize: 14 }}>
                          Change URL
                        </Text>
                      </TouchableOpacity>
                    </View>
                  ) : null}
                  <TouchableOpacity
                    style={[styles.uploadButton, { marginBottom: 12 }]}
                    onPress={() => void runYoutubeCheck()}
                    disabled={ytCheckLoading || breaksSaving}
                    activeOpacity={0.85}
                  >
                    {ytCheckLoading ? (
                      <ActivityIndicator color={theme.isDarkMode ? '#000' : '#fff'} />
                    ) : (
                      <Text style={styles.uploadButtonText}>Check URL</Text>
                    )}
                  </TouchableOpacity>
                  {ytInspect && !ytInspect.onMainChannel ? (
                    <>
                      <Text style={styles.label}>Video Title</Text>
                      <TextInput
                        style={styles.input}
                        value={ytVideoTitle}
                        onChangeText={setYtVideoTitle}
                        placeholder="Title for the video on your channel"
                        placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
                        autoCapitalize="sentences"
                      />
                    </>
                  ) : null}
                  <TouchableOpacity
                    style={[styles.button, styles.saveButton, { marginTop: 8 }]}
                    onPress={() => void saveYoutubeTab()}
                    disabled={breaksSaving}
                    activeOpacity={0.85}
                    {...(Platform.OS === 'web' ? { dataSet: { courseEditorPrimaryAction: 'true' } } : {})}
                  >
                    {breaksSaving ? (
                      <ActivityIndicator color={theme.isDarkMode ? '#000' : '#fff'} />
                    ) : (
                      <Text style={styles.saveButtonText}>Save to course breaks</Text>
                    )}
                  </TouchableOpacity>
                </>
              ) : null}

              {uploadModalTab === 'facebook' ? (
                <>
                  <Text style={styles.label}>Facebook URL</Text>
                  <TextInput
                    style={styles.input}
                    value={fbUrlInput}
                    onChangeText={setFbUrlInput}
                    placeholder="https://www.facebook.com/…"
                    placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
                    autoCapitalize="none"
                    autoCorrect={false}
                  />
                  <Text style={styles.label}>Video Title</Text>
                  <TextInput
                    style={styles.input}
                    value={fbVideoTitle}
                    onChangeText={setFbVideoTitle}
                    placeholder="Title for the video on your channel"
                    placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
                    autoCapitalize="sentences"
                  />
                  <Text style={[styles.helperMuted, { color: theme.colors.text }]}>
                    The video is downloaded on the server and uploaded to your main YouTube channel (same as Breaks Builder).
                  </Text>
                  <TouchableOpacity
                    style={[styles.button, styles.saveButton, { marginTop: 8 }]}
                    onPress={() => void saveFacebookTab()}
                    disabled={breaksSaving}
                    activeOpacity={0.85}
                    {...(Platform.OS === 'web' ? { dataSet: { courseEditorPrimaryAction: 'true' } } : {})}
                  >
                    {breaksSaving ? (
                      <ActivityIndicator color={theme.isDarkMode ? '#000' : '#fff'} />
                    ) : (
                      <Text style={styles.saveButtonText}>Save to course breaks</Text>
                    )}
                  </TouchableOpacity>
                </>
              ) : null}

              {uploadModalTab === 'file' ? (
                <>
                  {Platform.OS === 'web' ? (
                    <View style={{ marginBottom: 12 }}>
                      <input
                        ref={(el) => {
                          webBreakVideoInputRef.current = el;
                        }}
                        type="file"
                        accept="video/*"
                        style={{ display: 'none' }}
                        onChange={onWebBreakVideoFileChange}
                      />
                      <TouchableOpacity
                        style={styles.uploadButton}
                        onPress={() => webBreakVideoInputRef.current?.click?.()}
                        activeOpacity={0.85}
                        {...(Platform.OS === 'web' ? { dataSet: { courseEditorPrimaryAction: 'true' } } : {})}
                      >
                        <Text style={styles.uploadButtonText}>Choose video file</Text>
                      </TouchableOpacity>
                    </View>
                  ) : (
                    <TouchableOpacity
                      style={[styles.uploadButton, { marginBottom: 12 }]}
                      onPress={() => void pickBreakVideoFile()}
                      activeOpacity={0.85}
                    >
                      <Text style={styles.uploadButtonText}>Choose video file</Text>
                    </TouchableOpacity>
                  )}
                  {filePickMeta ? (
                    <View
                      style={styles.modalVideoFileRow}
                      {...(Platform.OS === 'web'
                        ? { dataSet: { courseEditorModalVideoFileRow: 'true' } }
                        : {})}
                    >
                      <Text style={[styles.modalVideoFileName, { color: theme.colors.text }]} numberOfLines={2}>
                        {filePickMeta.fileName}
                      </Text>
                      <TouchableOpacity
                        onPress={() => {
                          setFilePickMeta(null);
                          setFileBase64(null);
                          setFileVideoTitle('');
                        }}
                        style={styles.modalVideoFileRemoveHit}
                        hitSlop={12}
                        accessibilityRole="button"
                        accessibilityLabel="Remove file"
                      >
                        <Text style={styles.modalVideoFileRemoveText}>✕</Text>
                      </TouchableOpacity>
                    </View>
                  ) : null}
                  <Text style={styles.label}>Video Title</Text>
                  <TextInput
                    style={styles.input}
                    value={fileVideoTitle}
                    onChangeText={setFileVideoTitle}
                    placeholder="Title for the video on your channel"
                    placeholderTextColor={theme.isDarkMode ? '#888' : '#999'}
                    autoCapitalize="sentences"
                  />
                  <TouchableOpacity
                    style={[styles.button, styles.saveButton, { marginTop: 8 }]}
                    onPress={() => void saveFileTab()}
                    disabled={breaksSaving}
                    activeOpacity={0.85}
                    {...(Platform.OS === 'web' ? { dataSet: { courseEditorPrimaryAction: 'true' } } : {})}
                  >
                    {breaksSaving ? (
                      <ActivityIndicator color={theme.isDarkMode ? '#000' : '#fff'} />
                    ) : (
                      <Text style={styles.saveButtonText}>Save to course breaks</Text>
                    )}
                  </TouchableOpacity>
                </>
              ) : null}
            </ScrollView>
          </View>
        </View>
      </Modal>

      <Modal
        visible={!!videoModal}
        animationType="fade"
        transparent
        onRequestClose={closeVideoModal}
        supportedOrientations={['portrait', 'landscape']}
      >
        <View style={styles.ytModalRoot}>
          <Pressable style={styles.ytModalBackdrop} onPress={closeVideoModal} />
          <View
            style={[
              styles.ytModalSheet,
              {
                backgroundColor: theme.isDarkMode ? theme.colors.surface : '#111',
                borderColor: theme.isDarkMode ? theme.colors.border : '#333',
              },
            ]}
          >
            <View style={styles.ytModalHeader}>
              <Text style={[styles.ytModalTitle, { color: theme.colors.heading }]} numberOfLines={2}>
                {videoModal?.title ?? 'Video'}
              </Text>
              <TouchableOpacity
                onPress={closeVideoModal}
                style={styles.ytModalClose}
                hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
                accessibilityRole="button"
                accessibilityLabel="Close video"
              >
                <Text
                  style={[
                    styles.ytModalCloseText,
                    { color: theme.isDarkMode ? theme.colors.text : '#ffffff' },
                  ]}
                >
                  ✕
                </Text>
              </TouchableOpacity>
            </View>
            <View style={styles.ytWebViewWrap}>
              {videoModal?.uri ? (
                videoModal.isImage ? (
                  Platform.OS === 'web' ? (
                    <RNImage
                      source={{ uri: videoModal.uri }}
                      style={styles.ytModalImage}
                      resizeMode="contain"
                    />
                  ) : (
                    <Image
                      source={{ uri: videoModal.uri }}
                      style={styles.ytModalImage}
                      contentFit="contain"
                      transition={0}
                    />
                  )
                ) : (
                  <ModalVideoEmbed
                    uri={videoModal.uri}
                    title={videoModal.title}
                    webViewStyle={styles.ytWebView}
                  />
                )
              ) : null}
            </View>
          </View>
        </View>
      </Modal>

      <Stopwatch ref={stopwatchRef} />
    </View>
  );
}

function getStyles(theme, slideWidth) {
  const slideHeight = Math.round(slideWidth / CAROUSEL_ASPECT);
  return StyleSheet.create({
    ...createCourseMediaCarouselStyles(theme, slideWidth, slideHeight),
    container: {
      flex: 1,
      backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
    },
    header: {
      flexDirection: 'row',
      alignItems: 'center',
      paddingTop: 50,
      paddingBottom: 20,
      paddingHorizontal: 20,
      backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
    },
    hamburgerContainer: {
      marginRight: 15,
      backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
    },
    breadcrumbContainer: {
      flex: 1,
      backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
      minWidth: 0,
    },
    breadcrumbBack: {
      fontSize: 17,
      fontWeight: '600',
      color: theme.colors.heading,
    },
    profileIconContainer: {
      backgroundColor: theme.isDarkMode ? 'transparent' : '#FFFFFF',
    },
    content: {
      flex: 1,
      backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
    },
    contentContainer: {
      paddingHorizontal: 20,
      paddingTop: 12,
      paddingBottom: 48,
      flexGrow: 1,
      backgroundColor: theme.isDarkMode ? theme.colors.background : '#FFFFFF',
    },
    contentContainerWeb: Platform.select({
      web: { flexGrow: 1, minHeight: 'calc(100vh - 120px)' },
      default: {},
    }),
    centerBlock: {
      paddingVertical: 32,
      alignItems: 'center',
    },
    pageTitle: {
      fontSize: 22,
      fontWeight: '700',
      marginBottom: 18,
    },
    titleRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'baseline',
      marginBottom: 16,
    },
    courseTitle: {
      fontSize: 26,
      fontWeight: '700',
      flexShrink: 1,
    },
    fieldContainer: {
      marginBottom: 22,
      alignSelf: 'stretch',
    },
    label: {
      fontSize: 16,
      fontWeight: '600',
      color: theme.colors.text,
      marginBottom: 8,
    },
    selectCourseTrigger: {
      borderWidth: 1,
      borderColor: theme.colors.border,
      borderRadius: 8,
      overflow: 'hidden',
      backgroundColor: theme.isDarkMode ? theme.colors.surface : '#FFFFFF',
    },
    selectCourseTriggerInner: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingVertical: 14,
      paddingHorizontal: 14,
      backgroundColor: 'transparent',
    },
    selectCourseTriggerText: {
      flex: 1,
      fontSize: 15,
      color: theme.isDarkMode ? theme.colors.text : '#000000',
      marginRight: 10,
      backgroundColor: 'transparent',
    },
    selectCoursePlaceholder: {
      opacity: 0.65,
    },
    selectCourseTriggerChevron: {
      fontSize: 12,
      color: theme.isDarkMode ? theme.colors.heading : '#d4af37',
    },
    coursePickerSheet: {
      maxHeight: 440,
    },
    coursePickerScroll: {
      maxHeight: 360,
    },
    dropdownContainer: {
      borderWidth: 1,
      borderColor: theme.colors.border,
      borderRadius: 8,
      overflow: 'hidden',
      backgroundColor: theme.colors.surface,
      maxHeight: 260,
    },
    dropdownOption: {
      paddingVertical: 14,
      paddingHorizontal: 14,
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: theme.colors.border,
    },
    dropdownOptionSelected: {
      backgroundColor: theme.isDarkMode ? 'rgba(212,175,55,0.18)' : 'rgba(212,175,55,0.22)',
    },
    dropdownOptionText: {
      fontSize: 15,
      color: theme.colors.text,
      backgroundColor: 'transparent',
    },
    dropdownOptionTextSelected: {
      fontWeight: '700',
      color: theme.colors.heading,
      backgroundColor: 'transparent',
    },
    pickerModalFooter: {
      borderTopWidth: StyleSheet.hairlineWidth,
      borderTopColor: theme.colors.border,
      paddingTop: 12,
      marginTop: 4,
    },
    addCourseTypeRow: {
      flexDirection: 'row',
      alignItems: 'center',
      gap: 8,
    },
    addCourseTypeInput: {
      flex: 1,
      borderWidth: 1,
      borderRadius: 8,
      paddingVertical: 10,
      paddingHorizontal: 12,
      fontSize: 15,
      backgroundColor: theme.isDarkMode ? theme.colors.inputBackground : '#fff',
    },
    addCourseTypeBtn: {
      paddingVertical: 10,
      paddingHorizontal: 16,
      borderRadius: 8,
      backgroundColor: theme.colors.primary || '#d4af37',
    },
    addCourseTypeBtnText: {
      color: theme.isDarkMode ? '#000000' : '#FFFFFF',
      fontSize: 15,
      fontWeight: '700',
    },
    helperMuted: {
      fontSize: 14,
      lineHeight: 20,
      opacity: 0.72,
      marginBottom: 16,
    },
    summaryBlock: {
      marginBottom: 22,
      alignSelf: 'stretch',
    },
    summaryHeading: {
      fontSize: 17,
      fontWeight: '700',
      marginBottom: 8,
    },
    summaryTextArea: {
      minHeight: 220,
      borderWidth: 1,
      borderRadius: 8,
      padding: 12,
      fontSize: 15,
      lineHeight: 22,
      backgroundColor: theme.colors.inputBackground,
    },
    uploadButton: {
      backgroundColor: theme.colors.primary || '#d4af37',
      borderWidth: 0,
      paddingVertical: 14,
      borderRadius: 8,
      alignItems: 'center',
    },
    uploadButtonText: {
      color: theme.isDarkMode ? '#000000' : '#FFFFFF',
      fontSize: 16,
      fontWeight: '600',
    },
    pendingRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginTop: 10,
      paddingVertical: 10,
      paddingHorizontal: 12,
      borderRadius: 8,
      borderWidth: 1,
      borderColor: theme.colors.border,
      backgroundColor: theme.isDarkMode ? 'transparent' : theme.colors.surface,
    },
    pendingName: {
      flex: 1,
      fontSize: 14,
      color: theme.colors.text,
      marginRight: 10,
    },
    pendingRemoveHit: {
      minWidth: 36,
      minHeight: 36,
      alignItems: 'center',
      justifyContent: 'center',
      ...(theme.isDarkMode
        ? {}
        : {
            width: 32,
            height: 32,
            borderRadius: 16,
            backgroundColor: theme.colors.primary || '#d4af37',
          }),
    },
    pendingRemove: {
      fontSize: 18,
      color: theme.isDarkMode ? theme.colors.heading : '#ffffff',
      fontWeight: '600',
    },
    button: {
      paddingVertical: 15,
      paddingHorizontal: 16,
      borderRadius: 8,
      alignItems: 'center',
      alignSelf: 'stretch',
    },
    saveButton: {
      backgroundColor: theme.colors.primary || '#d4af37',
    },
    saveButtonText: {
      color: theme.isDarkMode ? '#000000' : '#FFFFFF',
      fontSize: 16,
      fontWeight: '600',
    },
    extSection: {
      marginTop: 8,
      marginBottom: 24,
      alignSelf: 'stretch',
    },
    extHeading: {
      fontSize: 17,
      fontWeight: '700',
      marginBottom: 10,
    },
    extCarouselOuter: {
      marginBottom: 4,
      alignSelf: 'stretch',
      width: '100%',
    },
    extThumbStrip: {
      marginTop: 12,
      width: '100%',
      alignSelf: 'stretch',
    },
    extThumbStripContent: {
      flexDirection: 'row',
      flexWrap: 'nowrap',
      alignItems: 'flex-start',
      justifyContent: 'center',
      flexGrow: 1,
      minWidth: '100%',
      paddingVertical: 8,
      paddingHorizontal: 4,
    },
    extThumbTile: {
      width: EXT_THUMB_TILE_WIDTH,
      marginHorizontal: EXT_THUMB_TILE_GAP / 2,
      alignItems: 'center',
    },
    extThumbTileFrame: {
      width: '100%',
      borderRadius: 8,
      borderWidth: 2,
      overflow: 'hidden',
      backgroundColor: theme.isDarkMode ? '#1a1a1a' : '#e8e8e8',
    },
    extThumbTileImage: {
      position: 'relative',
      width: '100%',
      aspectRatio: CAROUSEL_ASPECT,
    },
    extThumbFill: {
      ...StyleSheet.absoluteFillObject,
    },
    extMainSlideThumb: {
      position: 'relative',
      overflow: 'hidden',
      backgroundColor: theme.isDarkMode ? '#1a1a1a' : '#e8e8e8',
    },
    extThumbTileCaption: {
      fontSize: 11,
      lineHeight: 14,
      marginTop: 6,
      textAlign: 'center',
      width: '100%',
    },
    uploadModalRoot: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 20,
      backgroundColor: 'transparent',
    },
    uploadModalBackdrop: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: theme.isDarkMode ? 'rgba(0,0,0,0.5)' : 'transparent',
    },
    uploadModalSheet: {
      width: '100%',
      maxWidth: 440,
      maxHeight: 560,
      borderRadius: 12,
      borderWidth: 1,
      padding: 18,
      zIndex: 2,
    },
    uploadModalTitle: {
      fontSize: 18,
      fontWeight: '700',
      marginBottom: 14,
    },
    input: {
      backgroundColor: theme.colors.inputBackground,
      borderRadius: 8,
      padding: 12,
      fontSize: 16,
      color: theme.colors.text,
      borderWidth: 1,
      borderColor: theme.colors.inputBorder,
      marginBottom: 14,
    },
    inputLockedGrey: {
      opacity: 0.75,
      backgroundColor: theme.isDarkMode ? '#2a2a2a' : '#e8e8e8',
      color: theme.isDarkMode ? '#aaaaaa' : '#555555',
    },
    breakUploadToast: {
      flexDirection: 'row',
      alignItems: 'flex-start',
      paddingVertical: 12,
      paddingHorizontal: 12,
      borderRadius: 8,
      marginBottom: 12,
    },
    breakUploadToastError: {
      backgroundColor: '#c62828',
    },
    breakUploadToastSuccess: {
      backgroundColor: '#2e7d32',
    },
    breakUploadToastMessage: {
      flex: 1,
      color: '#ffffff',
      fontSize: 15,
      lineHeight: 20,
      marginRight: 8,
      paddingRight: 4,
      fontWeight: '700',
    },
    breakUploadToastClose: {
      padding: 4,
      marginTop: -2,
    },
    breakUploadToastCloseText: {
      color: '#ffffff',
      fontSize: 18,
      fontWeight: '700',
    },
    connectYoutubeBtn: {
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 14,
      paddingHorizontal: 14,
      borderRadius: 8,
      marginBottom: 10,
    },
    connectYoutubeStatusLoading: {
      alignItems: 'center',
      justifyContent: 'center',
      paddingVertical: 22,
      marginBottom: 10,
    },
    connectYoutubeLogo: {
      width: 44,
      height: 44 * (70 / 99),
      marginBottom: 8,
    },
    connectYoutubeBtnText: {
      fontSize: 15,
      fontWeight: '700',
    },
    connectYoutubeChannelLine: {
      fontSize: 13,
      marginBottom: 10,
      opacity: 0.85,
    },
    connectYoutubeWarningLine: {
      fontSize: 13,
      marginBottom: 8,
      lineHeight: 18,
      opacity: 0.9,
    },
    breakUploadTabs: {
      flexDirection: 'row',
      borderBottomWidth: StyleSheet.hairlineWidth,
      borderBottomColor: theme.colors.border,
      marginBottom: 14,
    },
    breakUploadTab: {
      flex: 1,
      paddingVertical: 10,
      paddingHorizontal: 4,
      alignItems: 'center',
      borderBottomWidth: 2,
      borderBottomColor: 'transparent',
    },
    breakUploadTabActive: {
      borderBottomColor: theme.colors.primary || '#d4af37',
    },
    breakUploadTabLabel: {
      fontSize: 12,
      fontWeight: '500',
      textAlign: 'center',
    },
    breakUploadTabLabelActive: {
      fontWeight: '700',
    },
    breakUploadScroll: {
      flexGrow: 0,
      maxHeight: 400,
    },
    breakUploadHintRow: {
      flexDirection: 'row',
      flexWrap: 'wrap',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 12,
      marginTop: -4,
    },
    breakUploadChannelHint: {
      flex: 1,
      minWidth: 120,
      fontSize: 13,
      lineHeight: 18,
      opacity: 0.85,
    },
    modalVideoFileRow: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      marginBottom: 14,
      marginTop: 4,
      paddingVertical: 10,
      paddingHorizontal: 12,
      borderRadius: 8,
      backgroundColor: theme.isDarkMode ? '#3a3a3a' : '#ececec',
      borderWidth: 1,
      borderColor: theme.colors.border,
    },
    modalVideoFileName: {
      flex: 1,
      fontSize: 14,
      marginRight: 10,
    },
    modalVideoFileRemoveHit: {
      minWidth: 36,
      minHeight: 36,
      alignItems: 'center',
      justifyContent: 'center',
    },
    modalVideoFileRemoveText: {
      fontSize: 18,
      fontWeight: '700',
      color: theme.colors.heading,
    },
    ytModalRoot: {
      flex: 1,
      justifyContent: 'center',
      alignItems: 'center',
      padding: 16,
    },
    ytModalBackdrop: {
      ...StyleSheet.absoluteFillObject,
      backgroundColor: 'rgba(0,0,0,0.65)',
    },
    ytModalSheet: {
      width: '100%',
      maxWidth: 720,
      borderRadius: 12,
      borderWidth: 1,
      overflow: 'hidden',
      zIndex: 2,
    },
    ytModalHeader: {
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      paddingHorizontal: 14,
      paddingVertical: 10,
    },
    ytModalTitle: {
      flex: 1,
      marginRight: 12,
      fontSize: 17,
      fontWeight: '600',
    },
    ytModalClose: {
      padding: 4,
      ...(theme.isDarkMode
        ? {}
        : {
            width: 34,
            height: 34,
            borderRadius: 17,
            backgroundColor: theme.colors.primary || '#d4af37',
            justifyContent: 'center',
            alignItems: 'center',
            padding: 0,
          }),
    },
    ytModalCloseText: {
      fontSize: 20,
      fontWeight: '600',
    },
    ytWebViewWrap: {
      width: '100%',
      height: Math.min(Math.round(slideWidth * 0.56), 400),
      minHeight: 220,
      backgroundColor: '#000',
    },
    ytWebView: {
      flex: 1,
      backgroundColor: '#000',
    },
    ytModalImage: {
      width: '100%',
      height: '100%',
      backgroundColor: '#000',
    },
  });
}
